<template>
  <Dialog
    :visible="true"
    modal
    :header="`Updated terms: ${subkrewe.name}`"
    :style="{ width: '32rem' }"
    :breakpoints="{ '575px': 'calc(100vw - 2rem)' }"
    :closable="false"
    :close-on-escape="false"
  >
    <div class="kb-form">
      <p class="kb-dialog-lead">
        <strong>{{ subkrewe.name }}</strong>
        {{ hasPriorAcceptance
          ? 'has updated their Terms & Conditions. Please review and accept the new terms to continue as a member.'
          : 'requires all members to accept the following Terms & Conditions to continue as a member.'
        }}
      </p>

      <!-- Long terms scroll inside the box; focusable so the keyboard can scroll it -->
      <div
        class="kb-form-note kb-policy kb-policy-scroll"
        role="region"
        :aria-label="`${subkrewe.name} Terms & Conditions`"
        tabindex="0"
      >
        {{ subkrewe.terms_and_conditions }}
      </div>

      <KbField
        v-slot="{ describedBy }"
        field-id="required-terms-acceptance"
        :error="acceptError"
      >
        <div class="kb-check-row">
          <Checkbox
            v-model="accepted"
            input-id="required-terms-acceptance"
            binary
            :invalid="Boolean(acceptError)"
            :pt="{ input: { 'aria-describedby': describedBy } }"
          />
          <label
            for="required-terms-acceptance"
          >
            I have read and accept the Terms &amp; Conditions above.
          </label>
        </div>
      </KbField>

      <p class="kb-field-hint">
        If you do not wish to accept, you can leave {{ subkrewe.name }}. You will remain a KreWeBacchus member and can join another SubKrewe at any time.
      </p>
    </div>

    <template #footer>
      <Button
        label="Leave SubKrewe"
        icon="pi pi-sign-out"
        text
        severity="danger"
        size="small"
        class="mr-auto"
        :loading="leaving"
        :disabled="saving"
        @click="onLeave"
      />
      <Button
        label="Accept"
        size="small"
        :loading="saving"
        :disabled="leaving"
        @click="onAccept"
      />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import type { Member } from '~/shared/types/member.js'
import type { SubKrewe } from '~/shared/types/subkrewe.js'
import { getAcceptance } from '~/shared/utils/subkreweTerms.js'

const props = defineProps<{
  member: Member
  subkrewe: SubKrewe
}>()

const toast = useToast()
const subkrewesStore = useSubKrewesStore()
const { recordAcceptance } = useSubkreweTerms()

const accepted = ref(false)
const submitted = ref(false)
const saving = ref(false)
const leaving = ref(false)

const hasPriorAcceptance = computed(() => !!getAcceptance(props.member, props.subkrewe.id))

const acceptError = computed(() =>
  submitted.value && !accepted.value ? 'You must accept the Terms & Conditions to continue.' : undefined)

const onAccept = async () => {
  submitted.value = true
  if (!accepted.value) return
  saving.value = true
  try {
    const ok = await recordAcceptance(props.member, props.subkrewe)
    if (!ok) throw new Error('Failed to record acceptance')
    toast.add({
      severity: 'success',
      summary: 'Terms Accepted',
      detail: `You've accepted the Terms & Conditions for ${props.subkrewe.name}.`,
      life: 3000,
    })
  }
  catch (error) {
    console.error('[RequiredSubkreweTerms] Failed to accept terms:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to save your acceptance. Please try again.',
      life: 4000,
    })
  }
  finally {
    saving.value = false
  }
}

const onLeave = async () => {
  leaving.value = true
  try {
    const ok = await subkrewesStore.removeMemberFromSubKrewe(
      props.subkrewe,
      props.member,
      props.member.id,
      'Declined updated SubKrewe Terms & Conditions',
    )
    if (!ok) throw new Error('Failed to leave SubKrewe')
    toast.add({
      severity: 'info',
      summary: 'Left SubKrewe',
      detail: `You have left ${props.subkrewe.name}.`,
      life: 4000,
    })
  }
  catch (error) {
    console.error('[RequiredSubkreweTerms] Failed to leave SubKrewe:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to leave the SubKrewe. Please try again.',
      life: 4000,
    })
  }
  finally {
    leaving.value = false
  }
}
</script>
